import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { statsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const LEVEL_LABELS = {
  olympic: '🏅 الألعاب الأولمبية',
  world: '🌍 بطولة العالم',
  world_cup: '🏆 كأس العالم',
  grand_prix: '🎖️ جائزة كبرى',
  continental: '🌐 بطولة قارية',
  arab: '🌙 بطولة عربية',
  gulf: '⛽ بطولة خليجية',
  national: '🏳️ بطولة وطنية',
  local: '📍 بطولة محلية'
};

const th = { padding:'10px 8px', textAlign:'center', fontWeight:700, fontSize:12, color:'var(--text-secondary)', borderBottom:'2px solid var(--border)' };
const td = { padding:'10px 8px', verticalAlign:'middle', textAlign:'center' };
const inp = { background:'var(--bg-secondary)', border:'1px solid var(--gold)', borderRadius:6, padding:'4px 8px', color:'var(--text-primary)', width:75, textAlign:'center', fontFamily:'inherit' };

export default function PointRules() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ['point-rules'],
    queryFn: () => statsAPI.pointRules().then(r => r.data.data)
  });

  const mut = useMutation({
    mutationFn: ({ id, participation_points, placement_points, medal_points }) =>
      statsAPI.updatePointRule(id, { participation_points, placement_points, medal_points }),
    onSuccess: () => {
      toast.success('تم الحفظ');
      queryClient.invalidateQueries(['point-rules']);
      setEdits({});
    },
    onError: (e) => toast.error(e.response?.data?.message || 'خطأ')
  });

  if (isLoading) return <div className="loading"><div className="spinner"/></div>;

  const byLevel = {};
  (data || []).forEach(r => {
    if (!byLevel[r.tournament_level]) byLevel[r.tournament_level] = [];
    byLevel[r.tournament_level].push(r);
  });
  Object.keys(byLevel).forEach(l => byLevel[l].sort((a,b) => a.placement - b.placement));

  const get = (rule, field) => edits[rule.id]?.[field] ?? rule[field] ?? 0;
  const set = (rule, field, val) => setEdits(p => ({ ...p, [rule.id]: { ...(p[rule.id]||{}), [field]: val } }));
  const save = (rule) => mut.mutate({ id: rule.id, participation_points: get(rule,'participation_points'), placement_points: get(rule,'placement_points'), medal_points: get(rule,'medal_points') });
  const isDirty = (rule) => edits[rule.id] !== undefined;

  return (
    <div>
      <div className="topbar"><span className="topbar-title">⚙️ قواعد النقاط</span></div>
      <div className="page-content">
        {isAdmin && <div style={{background:'rgba(201,168,76,0.1)',border:'1px solid rgba(201,168,76,0.3)',borderRadius:8,padding:'10px 16px',marginBottom:20,fontSize:13,color:'var(--gold)'}}>✏️ أنت في وضع التحرير — عدّل أي قيمة واضغط حفظ</div>}
        {Object.entries(LEVEL_LABELS).map(([level, label]) => {
          const rules = byLevel[level];
          if (!rules?.length) return null;
          const partPts = rules[0]?.participation_points ?? 0;
          return (
            <div key={level} className="card" style={{marginBottom:24}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
                <h2 style={{fontSize:18,margin:0}}>{label}</h2>
                <div style={{fontSize:13,color:'var(--text-secondary)'}}>نقاط المشاركة الثابتة: <b style={{color:'var(--gold)'}}>{partPts}</b> نقطة</div>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13,direction:'rtl'}}>
                  <thead>
                    <tr style={{background:'var(--bg-secondary)'}}>
                      <th style={th}>م</th>
                      <th style={th}>البند</th>
                      <th style={{...th,color:'var(--gold)'}}>نقاط المشاركة</th>
                      <th style={{...th,color:'var(--cyan)'}}>نقاط الترتيب</th>
                      <th style={{...th,color:'#ffd700'}}>🥇 ذهبية</th>
                      <th style={{...th,color:'#c0c0c0'}}>🥈 فضية</th>
                      <th style={{...th,color:'#cd7f32'}}>🥉 برونزية</th>
                      <th style={{...th,color:'var(--gold)'}}>الإجمالي</th>
                      {isAdmin && <th style={th}>حفظ</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule, idx) => {
                      const pp = parseFloat(get(rule,'participation_points'))||0;
                      const plp = parseFloat(get(rule,'placement_points'))||0;
                      const mp = parseFloat(get(rule,'medal_points'))||0;
                      const total = pp + plp + mp;
                      return (
                        <tr key={rule.id} style={{borderBottom:'1px solid var(--border)',background:idx%2===0?'transparent':'rgba(255,255,255,0.02)'}}>
                          <td style={td}>{idx+1}</td>
                          <td style={{...td,textAlign:'right'}}>
                            <div style={{fontWeight:600}}>{label} — المركز {rule.placement}</div>
                            <div style={{fontSize:11,color:rule.medal_type==='gold'?'#ffd700':rule.medal_type==='silver'?'#c0c0c0':rule.medal_type==='bronze'?'#cd7f32':'var(--text-muted)',marginTop:2}}>
                              {rule.medal_type==='gold'?'🥇 ذهبية':rule.medal_type==='silver'?'🥈 فضية':rule.medal_type==='bronze'?'🥉 برونزية':'🏅 مشاركة'}
                            </div>
                          </td>
                          <td style={td}>
                            {isAdmin ? <input style={inp} type="number" value={get(rule,'participation_points')} onChange={e=>set(rule,'participation_points',e.target.value)}/> : <b style={{color:'var(--gold)'}}>{rule.participation_points}</b>}
                          </td>
                          <td style={td}>
                            {isAdmin ? <input style={inp} type="number" value={get(rule,'placement_points')} onChange={e=>set(rule,'placement_points',e.target.value)}/> : <b style={{color:'var(--cyan)'}}>{rule.placement_points}</b>}
                          </td>
                          <td style={td}>
                            {isAdmin ? <input style={{...inp,borderColor:'#ffd700'}} type="number" value={get(rule,'medal_points')} onChange={e=>set(rule,'medal_points',e.target.value)}/> : <b style={{color:'#ffd700'}}>{rule.medal_points}</b>}
                          </td>
                          <td style={td}>
                            {isAdmin ? <input style={{...inp,borderColor:'#c0c0c0'}} type="number" value={get(rule,'medal_points')} onChange={e=>set(rule,'medal_points',e.target.value)}/> : <b style={{color:'#c0c0c0'}}>{rule.medal_points}</b>}
                          </td>
                          <td style={td}>
                            {isAdmin ? <input style={{...inp,borderColor:'#cd7f32'}} type="number" value={get(rule,'medal_points')} onChange={e=>set(rule,'medal_points',e.target.value)}/> : <b style={{color:'#cd7f32'}}>{rule.medal_points}</b>}
                          </td>
                          <td style={td}><b style={{color:'var(--gold)',fontSize:15}}>{total}</b></td>
                          {isAdmin && <td style={td}>{isDirty(rule) && <button className="btn btn-primary btn-sm" onClick={()=>save(rule)} disabled={mut.isPending}>💾 حفظ</button>}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
